import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { Modal } from '../components/Modal'
import { useDb } from '../state/DbProvider'
import { getCurrentUser } from '../state/selectors'
import { formatJPY, formatPercent } from '../utils/format'
import type { Product } from '../state/types'
import { getProductPriceLabel, isAdSlotProduct } from '../utils/products'
import {
  calculateRewardBreakdown,
  getEffectiveAgencyRateForProduct,
  getEffectiveConnectorRateForProduct,
  getEffectiveSuperAgencyRate,
} from '../utils/reward'

export default function MarketplacePage() {
  const { db } = useDb()
  const [openId, setOpenId] = useState<string | null>(null)

  if (!db) return null
  const me = getCurrentUser(db)

  const products = db.products
    .filter((product) => product.isPublic)
    .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
  const openProduct = products.find((product) => product.id === openId) ?? null
  const canRegisterDeal = me.role === '総代理店' || me.role === '代理店' || me.role === 'コネクター'
  const createDealLink = openProduct
    ? `/deals?${new URLSearchParams({ openCreate: '1', productId: openProduct.id }).toString()}`
    : '/deals'

  return (
    <div className="grid gap-4">
      <Card title="商品一覧" subtitle="公開中の商品から、そのまま商談登録へ進めます。">
        <div className="px-5 py-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {products.map((product) => (
              <div key={product.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-base font-semibold text-slate-900">{product.name}</div>
                    <div className="mt-1 text-sm text-slate-600">{getProductPriceLabel(product)}</div>
                  </div>
                </div>

                <div className="mt-3 text-sm leading-6 text-slate-700">
                  {product.description || '説明は未登録です。'}
                </div>

                {isAdSlotProduct(product) && product.vacancyStatus ? (
                  <div className="mt-3 text-xs text-slate-500">空き状況: {product.vacancyStatus}</div>
                ) : null}

                <div className="mt-4">
                  <Button variant="primary" onClick={() => setOpenId(product.id)}>
                    詳細を見る
                  </Button>
                </div>
              </div>
            ))}

            {products.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                公開中の商品はまだありません。
              </div>
            ) : null}
          </div>
        </div>
      </Card>

      <Modal
        open={!!openProduct}
        onClose={() => setOpenId(null)}
        title={openProduct?.name ?? '商品詳細'}
        description={openProduct ? getProductPriceLabel(openProduct) : undefined}
      >
        {openProduct ? (
          <div className="grid gap-4">
            <div className="space-y-2 text-sm text-slate-700">
              <p className="whitespace-pre-wrap leading-6">{openProduct.description || '説明は未登録です。'}</p>
              {isAdSlotProduct(openProduct) && openProduct.adSpec ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-semibold text-slate-900">広告枠情報</div>
                  <div className="mt-2 grid gap-1 text-sm text-slate-700">
                    <div>所在地: {openProduct.adSpec.address}</div>
                    <div>サイズ: {openProduct.adSpec.size}</div>
                    <div>放映頻度: {openProduct.adSpec.playbackFrequency}</div>
                    {openProduct.vacancyStatus ? <div>空き状況: {openProduct.vacancyStatus}</div> : null}
                    {openProduct.adSpec.mapUrl ? (
                      <a
                        href={openProduct.adSpec.mapUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="w-fit text-blue-600 hover:underline"
                      >
                        地図を開く
                      </a>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-900">この商品で商談を始める</div>
              {canRegisterDeal ? (
                <div className="mt-2 grid gap-3">
                  <div className="text-sm text-slate-700">
                    この商品を選んだ状態で商談登録画面を開きます。必要事項を入力して登録してください。
                  </div>
                  <Link
                    to={createDealLink}
                    onClick={() => setOpenId(null)}
                    className="inline-flex w-fit items-center rounded-lg bg-[var(--jnavi-navy)] px-3 py-2 text-sm text-white hover:opacity-90"
                  >
                    商談登録へ進む
                  </Link>
                </div>
              ) : (
                <div className="mt-2 text-sm text-slate-700">商談登録は総代理店、代理店、コネクターでログインすると利用できます。</div>
              )}
            </div>

            <CommissionPreview product={openProduct} />
          </div>
        ) : null}
      </Modal>
    </div>
  )
}

function CommissionPreview(props: { product: Product }) {
  const { db } = useDb()
  const me = db ? getCurrentUser(db) : null

  if (!db || !me || (me.role !== '総代理店' && me.role !== '代理店' && me.role !== 'コネクター')) return null

  const numericBase = Math.max(0, Math.floor(props.product.listPriceJPY || 0))
  const hasProductRate = props.product.agencyRate !== undefined || props.product.connectorRate !== undefined
  const superAgencyRate = getEffectiveSuperAgencyRate(db.settings)
  const agencyRate = getEffectiveAgencyRateForProduct(props.product, db.settings, me.role === '代理店' ? me : undefined)
  const connectorRate = getEffectiveConnectorRateForProduct(props.product, db.settings, me.role === 'コネクター' ? me : undefined)
  const hasConnector = me.role === 'コネクター'
  const amounts = calculateRewardBreakdown({
    baseAmountJPY: numericBase,
    superAgencyRate,
    agencyRate,
    connectorRate,
    assignedAgencyRole: me.role === '総代理店' ? '総代理店' : '代理店',
    hasSuperAgency: false,
    hasConnector,
  })

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-sm font-semibold text-slate-900">報酬シミュレーション</div>
      <div className="mt-3 grid gap-3">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-600">設定金額</span>
            <span className="font-semibold">{numericBase > 0 ? formatJPY(numericBase) : '価格未設定'}</span>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            報酬は管理者が商品マスタで設定した金額をもとに計算します。
          </div>
          {hasProductRate ? (
            <div className="mt-1 text-xs text-amber-700">この商品には個別の報酬率が設定されています。</div>
          ) : null}
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
          {me.role === '総代理店' ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">総代理店直案件の想定報酬（{formatPercent(amounts.superAgencyAllocationRate)}）</span>
                <span className="font-semibold">{formatJPY(amounts.superAgencyRewardJPY)}</span>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                配下代理店案件として登録する場合は、商談登録画面で担当代理店を選ぶと 10% / 15% / 5% の階層配分になります。
              </div>
            </>
          ) : me.role === '代理店' ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">代理店直案件の想定報酬（{formatPercent(amounts.agencyAllocationRate)}）</span>
                <span className="font-semibold">{formatJPY(amounts.agencyRewardJPY)}</span>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                コネクターが付く案件は、ここから担当コネクター率を差し引いた金額が代理店取り分になります。
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">コネクター報酬の想定額（{formatPercent(connectorRate)}）</span>
                <span className="font-semibold">{formatJPY(amounts.connectorRewardJPY)}</span>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                実際の報酬額は、管理者が設定した商品金額と担当代理店または総代理店の設定で確定します。
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
